import type { ItemForUser } from '@shared/types';
import { ICON_LABEL, type IconSymbol } from '@shared/types';

/**
 * Das Symbol eines Eintrags.
 *
 * Drei Stufen, in dieser Reihenfolge:
 *   1. ein bewusst gewaehltes Sinnbild
 *   2. das Favicon des Anbieters (liegt als Bilddaten am Eintrag)
 *   3. der Anfangsbuchstabe
 *
 * Die Sinnbilder sind fest eingebaute Zeichnungen: sie laden nichts nach,
 * passen sich der Textfarbe an und funktionieren damit auch im Dunkelmodus.
 */

const PATHS: Record<IconSymbol, JSX.Element> = {
  wlan: (
    <>
      <path d="M2 8.5a15 15 0 0 1 20 0" />
      <path d="M5.5 12a10 10 0 0 1 13 0" />
      <path d="M9 15.5a5 5 0 0 1 6 0" />
      <circle cx="12" cy="19" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),
  server: (
    <>
      <rect x="3" y="4" width="18" height="7" rx="1.5" />
      <rect x="3" y="13" width="18" height="7" rx="1.5" />
      <path d="M7 7.5h.01M7 16.5h.01" />
    </>
  ),
  netzwerk: (
    <>
      <rect x="9" y="3" width="6" height="5" rx="1" />
      <rect x="2" y="16" width="6" height="5" rx="1" />
      <rect x="16" y="16" width="6" height="5" rx="1" />
      <path d="M12 8v4M5 16v-2h14v2" />
    </>
  ),
  schloss: (
    <>
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
  gebaeude: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="1.5" />
      <path d="M9 7h.01M15 7h.01M9 11h.01M15 11h.01M9 15h.01M15 15h.01" />
      <path d="M10 21v-3h4v3" />
    </>
  ),
  drucker: (
    <>
      <path d="M7 8V3h10v5" />
      <rect x="3" y="8" width="18" height="8" rx="1.5" />
      <rect x="7" y="14" width="10" height="7" rx="1" />
    </>
  ),
  kamera: (
    <>
      <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2L8 5h8l1.5 2h2A1.5 1.5 0 0 1 21 8.5v10A1.5 1.5 0 0 1 19.5 20h-15A1.5 1.5 0 0 1 3 18.5z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  musik: (
    <>
      <path d="M9 18V5l11-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="17" cy="16" r="3" />
    </>
  ),
  cloud: <path d="M7 18a4.5 4.5 0 0 1-.5-8.97A6 6 0 0 1 18 9.5a4.25 4.25 0 0 1-.5 8.5z" />,
  karte: (
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="M2.5 10h19M6 15h4" />
    </>
  ),
  werkzeug: (
    <path d="M14.5 5.5a4 4 0 0 0 5 5l-9 9a2.8 2.8 0 0 1-4-4z" />
  ),
  stern: (
    <path d="M12 3.5l2.6 5.5 5.9.8-4.3 4.2 1 6-5.2-2.8L6.8 20l1-6-4.3-4.2 5.9-.8z" />
  ),
};

export function SymbolIcon({ name, size = 20 }: { name: IconSymbol; size?: number }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label={ICON_LABEL[name]}
    >
      {PATHS[name]}
    </svg>
  );
}

export function ItemIcon({
  item,
  size = 22,
}: {
  item: Pick<ItemForUser, 'iconSymbol' | 'iconData' | 'title'>;
  size?: number;
}): JSX.Element {
  if (item.iconSymbol && item.iconSymbol in PATHS) {
    return (
      <span className="item-icon" style={{ width: size, height: size }}>
        <SymbolIcon name={item.iconSymbol as IconSymbol} size={Math.round(size * 0.8)} />
      </span>
    );
  }

  if (item.iconData) {
    return (
      <img
        className="item-icon item-icon-img"
        src={item.iconData}
        alt=""
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span className="item-icon item-icon-letter" style={{ width: size, height: size }}>
      {(item.title || '?').charAt(0).toUpperCase()}
    </span>
  );
}
